import { CustomerLocationSelectors } from "@app/customer-portal/store/selectors";
import { ProjectSelectors } from "@app/project/store";
import { initialRuleState } from "@app/project/store/clarehome.state";
import { RouterSelectors } from "@app/root-store";
import { CHApiResultState, LoadingState } from "@app/root-store/api-result.state";
import { CommandProviders } from "@app/rule/model/commands/command";
import { SceneCommand, SceneCommandHelper, isSceneCommand } from "@app/rule/model/commands/scene-command";
import { ConditionProviders } from "@app/rule/model/conditions/condition";
import { CHRuleInt } from "@app/rule/model/rule";
import { ClareHomeDbCollection } from "@app/shared/resources/resource-objectifier";
import { createFeatureSelector, createSelector } from "@ngrx/store";
import { CHRuleState } from "../rule.state";
import { selectScenes } from "./commands.selectors";

const selectState = createFeatureSelector<CHRuleState>("chrule");

const selectChRules = (state: CHRuleState, uuid: string) => (state?.uuid ? state : initialRuleState(uuid));

export const getChRules = createSelector(selectState, ProjectSelectors.getUuid, (state: CHRuleState, uuid: string) =>
    selectChRules(state, uuid)
);

export const getCurrentRule = createSelector(getChRules, RouterSelectors.selectUrlParam("ruleid"), (ruleState, ruleId: string) => ({
    uuid: ruleState.uuid,
    callState: ruleState.callState,
    result: ruleState.result ? ruleState.result[ruleId] : null,
}));

export const getLocationDataState = createSelector(
    CustomerLocationSelectors.getLocationDataState,
    ProjectSelectors.getUuid,
    (locationData, uuid) =>
        locationData?.[uuid]
            ? locationData[uuid]
            : {
                  uuid,
                  callState: LoadingState.init,
              }
);

const getRuleType = (rule: CHRuleInt) => {
    let ruleType = rule.conditionType;

    switch (rule.conditionType) {
        case "AbsoluteTimerCondition":
        case "PeriodTimerCondition":
        case "IntervalTimerCondition":
        case "DailyTimerCondition":
        case "AstroTimerCondition":
            ruleType = "Schedule";
            break;

        case "UICondition":
            ruleType = "Scene";
            break;

        case "MultiCondition":
        case "PropertyChangeCondition":
            if (isSceneCommand(rule.command) && SceneCommandHelper.hasOnlyNotificationCommands(rule.command)) {
                ruleType = "Notification";
            } else {
                ruleType = "Automation";
            }
            break;

        default:
    }

    return ruleType;
};

export const getRuleStatusCounts = createSelector(getChRules, (ruleState: CHApiResultState<ClareHomeDbCollection<CHRuleInt>>) => {
    const statusitems = { Notification: 0, Automation: 0, Scene: 0, Schedule: 0 };

    for (const id in ruleState.result) {
        if (ruleState.result[id]) {
            const type = getRuleType(ruleState.result[id]);
            statusitems[type]++;
        }
    }

    return statusitems;
});

export const getUIAndDealerScenes = createSelector(selectScenes, getChRules, (sceneCommands: SceneCommand[], ruleState) => {
    const scenes = sceneCommands.reduce((prevScenes, scene) => {
        if (!scene.createdByMch) {
            return [...prevScenes, { name: scene.name, commandId: scene.commandId, commandType: CommandProviders.SCENE }];
        }

        return prevScenes;
    }, []);

    if (ruleState?.result && sceneCommands.length) {
        for (const uid in ruleState.result) {
            if (
                ruleState.result[uid].conditionType === ConditionProviders.UI_CONDITION &&
                sceneCommands.some(scene => scene.commandId === ruleState.result[uid].commandId)
            ) {
                scenes.push({
                    name: ruleState.result[uid].name,
                    commandId: ruleState.result[uid].commandId,
                    commandType: CommandProviders.SCENE,
                });
            }
        }
    }

    scenes.sort((a, b) => a.name.localeCompare(b.name));

    return scenes;
});
